const fold = (value) =>
  (value ?? '')
    .toString()
    .replace(/&amp;/gi, '&')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
// Room keys store labels lowercased and without spaces ("breakfast,lunch&dinner",
// "roomonly"), so labels are compared without any whitespace.
const normalize = (value) => fold(value).replace(/\s+/g, '');
const day = (value) => (value ?? '').toString().slice(0, 10);

const namesOf = (entry) => [
  entry.enDisplayName,
  entry.displayName,
  ...(entry.labels ?? []),
];

// Booking tags and room keys freeze the label in the language of the search
// that made them; every translation served by the API is recognised.
export function translateAccommodationLabel(label, entries) {
  if (!label) return label;
  const key = normalize(label);
  const hit = entries?.find((entry) =>
    namesOf(entry).some((name) => name && normalize(name) === key),
  );
  return hit?.displayName || hit?.enDisplayName || label;
}

// The night of `dayDate` belongs to the booking checked in on or before it
// and checked out after it.
export function findBookingForDay(bookings, dayDate) {
  const night = day(dayDate);
  if (!night) return undefined;
  return bookings?.find(
    (booking) => day(booking.startAt) <= night && night < day(booking.endAt),
  );
}

// The meals a board label mentions, in the supplier languages. Same words as
// the API's mealPlanCodeFromLabel (booking-core/sources/mealPlanCode.ts).
// ponytail: duplicated there, keep both in step if a language is added.
function mealsOf(label) {
  const text = fold(label).replace(
    /petit[\s-]*dejeuner|petit[\s-]*dej\b|pequeno[\s-]*almoco|cafe da manha/g,
    'breakfast',
  );
  if (
    /all[\s-]*incl|tout[\s-]*(compris|inclus)|todo[\s-]*incluido|alles[\s-]*inklusive/.test(
      text,
    )
  )
    return 'all-inclusive';
  if (/room[\s-]*only|chambre[\s-]*seule|logement[\s-]*seul/.test(text))
    return 'room-only';
  const meals = [
    /breakfast|\bbb\b|desayuno|fruhstuck|colazione|ontbijt/.test(text) && 'B',
    /lunch|\bdejeuner\b|almuerzo|mittagessen|pranzo/.test(text) && 'L',
    /dinner|\bdiner\b|\bcena\b|abendessen|\bjantar\b/.test(text) && 'D',
  ].filter(Boolean);
  if (!meals.length) return '';
  const choice = meals.length > 2 && /\b(or|ou|o|oder|of)\b/.test(text);
  return meals.join('') + (choice ? '?' : '');
}

// A supplier board (Koedia) is not in the dictionary: it maps onto the Koob
// plan serving the same meals, the plainest name first.
function translateByMeals(label, entries) {
  const meals = mealsOf(label);
  if (!meals) return undefined;
  const hit = entries
    ?.filter((entry) => mealsOf(entry.enDisplayName) === meals)
    .sort((a, b) => a.enDisplayName.length - b.enDisplayName.length)[0];
  return hit && (hit.displayName || hit.enDisplayName);
}

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

// A meal plan label is matched as a whole first ("Breakfast & Dinner" is one
// plan), then by the meals it serves, then part by part.
export function translateMealPlan(label, entries) {
  if (!label) return label;
  const whole = translateAccommodationLabel(label, entries);
  if (whole !== label) return whole;
  const byMeals = translateByMeals(label, entries);
  if (byMeals) return byMeals;
  return label
    .split(/[,&]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => capitalize(translateAccommodationLabel(part, entries)))
    .join(', ');
}
